import {
  DateToShortOrLongReadable,
  FormatAsCurrencyForLocale
} from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { toSentenceCasing } from './formatString';

const DISPLAY_TAG_PATTERN = /(?:\{\{|\[\[|\|\|)\s*([^}\]|]+)\s*(?:\}\}|\]\]|\|\|)/g;
const OBJECT_TAG_PATTERN = /<object[^>]*\bdata-codename="([^"]+)"[^>]*>(?:<\/object>)?/gi;

const PLACEHOLDER_VALUE_TYPES = {
  TEXT: 'text',
  CURRENCY: 'currency',
  DATE_SHORT: 'dateShort'
};

export const getValueByKey = (source = {}, key = '') => {
  if (!source || typeof source !== 'object' || !key) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }

  const caseInsensitiveMatch = Object.keys(source).find((k) => k.toLowerCase() === key.toLowerCase());
  return caseInsensitiveMatch ? source[caseInsensitiveMatch] : undefined;
};

export const replaceDisplayTagPlaceholders = (
  text = '',
  values = {},
  placeholderMap = {},
  shouldLowercasePlaceholderValues = false
) => {
  if (typeof text !== 'string' || !text) return text;
  if (!values || typeof values !== 'object') return text;

  return text.replace(DISPLAY_TAG_PATTERN, (_, key) => {
    const placeholderKey = key.trim();
    const value = getValueByKey(values, placeholderMap?.[placeholderKey]?.apiKey ?? placeholderKey);
    const valueType = placeholderMap?.[placeholderKey]?.valueType || PLACEHOLDER_VALUE_TYPES.TEXT;
    if (value === null || value === undefined) return '';
    if (valueType === PLACEHOLDER_VALUE_TYPES.CURRENCY)
      return FormatAsCurrencyForLocale(value, { trimZeroDecimals: true });
    if (valueType === PLACEHOLDER_VALUE_TYPES.DATE_SHORT) return DateToShortOrLongReadable(value);

    const stringValue = String(value);
    if (stringValue.toLowerCase() === 'booster care' || stringValue.toLowerCase() === 'extra care') return stringValue;
    return shouldLowercasePlaceholderValues ? stringValue.toLowerCase() : toSentenceCasing(stringValue);
  });
};

// Replaces <a data-item-id="...">...</a> with tooltip HTML markup for string-based rendering
export const resolveTooltipContent = (content, tooltipsContent = [], tooltipModalBody = []) => {
  if (typeof content !== 'string' || !content) return content;
  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const toPlainText = (value = '') =>
    String(value)
      .replace(/<[^>]*>/g, '')
      .trim();

  const flattenTooltipItems = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;

    if (typeof input === 'object') {
      return Object.values(input).flatMap((item) => {
        if (!item) return [];
        if (Array.isArray(item)) return item;
        if (typeof item === 'object') {
          // Handles shapes like { routinecareuniversal: { id, heading, value } }
          if (Object.prototype.hasOwnProperty.call(item, 'id')) return [item];
          return Object.values(item).filter((nested) => nested && typeof nested === 'object');
        }
        return [];
      });
    }

    return [];
  };

  const tooltipItems = flattenTooltipItems(tooltipsContent);
  const tooltipModalBodyItems = flattenTooltipItems(tooltipModalBody);
  const tooltipAnchorPattern = /<a\s+[^>]*data-item-id=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;

  return content.replace(tooltipAnchorPattern, (_, itemId, anchorText) => {
    const tooltipObj = tooltipItems.find((item) => String(item?.id) === String(itemId));
    if (!tooltipObj) return ''; // If no matching tooltip is found, remove the anchor tag from the output
    const headingText = toPlainText(tooltipObj.heading || anchorText || "What's this?");
    const ariaLabel = headingText || "What's this?";
    const tooltipBody = String(tooltipObj.value || "What's this?");
    const transformedTooltipBody = tooltipBody
      .replace(/<p>/g, '<span class="tooltip-text">')
      .replace(/<\/p>/g, '</span>');
    let innerHtml = transformedTooltipBody;

    if (tooltipModalBodyItems.length > 0) {
      tooltipObj?.value.replace(tooltipAnchorPattern, (_, itemId, anchorText) => {
        const modalTooltipObj = tooltipModalBodyItems.find((item) => String(item?.id) === String(itemId));
        if (!modalTooltipObj) return ''; // If no matching modular tooltip is found, remove the anchor tag from the output
        innerHtml = `<button type="button" class="tooltip-modal-link" data-tooltip-modal-title="${escapeHtml(
          modalTooltipObj?.heading
        )}" data-tooltip-modal-body="${escapeHtml(modalTooltipObj?.value)}" aria-label="Open details for ${escapeHtml(
          modalTooltipObj?.heading
        )}">${transformedTooltipBody}</button>`;
        return innerHtml;
      });
    }

    return `
      <span class="tooltip-trigger-wrapper tooltip-click-balloon" data-item-id="${escapeHtml(itemId)}">
        <button type="button" aria-expanded="false" aria-label="${escapeHtml(
          ariaLabel
        )}" class="tooltip-icon-trigger" data-item-id="${escapeHtml(itemId)}">
          <i class="icon icon-information" role="presentation" aria-hidden="true"></i>
        </button>
          <span role="tooltip" class="tooltip-click-balloon-content">
            ${innerHtml}
          </span>
      </span>`;
  });
};

export const decodeHtmlEntities = (value = '') => {
  if (!value || typeof window === 'undefined') return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

export const replaceSummaryObjects = (text = '', values = {}, rawContent = {}) => {
  if (typeof text !== 'string' || !text) return text;
  if (!values || typeof values !== 'object') return text;

  const keyNames = [
    resolveContentComparison(values?.Excess, values?.csp_Excess, 'annual_excess'),
    resolveContentComparison(values?.BenefitPercentage, values?.csp_BenefitPercentage, 'benefit_percentage'),
    resolveContentComparison(values?.PremiumAnnual, values?.csp_NewPremium, 'submit_premium_state'),
    resolveConfirmationContentComparison(values?.PreferredContactMethod)
  ];

  return text.replace(OBJECT_TAG_PATTERN, (_, codename) => {
    const placeholderKey = codename.trim();
    if (!keyNames.includes(placeholderKey)) return '';
    return rawContent?.[placeholderKey]?.value || '';
  });
};

export const resolveConfirmationContentComparison = (value) => {
  if (value === 'Post') return 'contact_method_post';
  if (value === 'Email') return 'contactmethodemail';
  return '';
};
export const resolveContentComparison = (oldValue, newValue, name) => {
  if (newValue > oldValue) return `${name}_increase`;
  if (newValue < oldValue) return `${name}_decrease`;
  return `${name}_original`;
};

export const replacePanelSummaryObjects = (values = {}, rawContent = {}) => {
  if (!values || typeof values !== 'object') return '';

  const panelSummaryCodename = resolvePanelSummaryContentComparison(
    values?.premiumAnnualOldValue,
    values?.premiumAnnualNewValue,
    values?.premium,
    values?.csp_newPremium,
    'panel_summary'
  );

  return rawContent?.[panelSummaryCodename]?.value || '';
};

export const resolvePanelSummaryContentComparison = (
  premiumAnnualOldValue,
  premiumAnnualNewValue,
  premiumOldValue,
  premiumNewValue,
  name
) => {
  if (premiumAnnualOldValue > premiumAnnualNewValue && premiumOldValue < premiumNewValue) return `${name}_decrease`;
  if (premiumAnnualOldValue < premiumAnnualNewValue && premiumOldValue > premiumNewValue) return `${name}_increase`;

  if (premiumAnnualNewValue > premiumAnnualOldValue && premiumOldValue === premiumNewValue)
    return `${name}_increase_out_of_pocket_no_change`;
  if (premiumAnnualNewValue < premiumAnnualOldValue && premiumOldValue === premiumNewValue)
    return `${name}_decrease_out_of_pocket_no_change`;
  if (premiumAnnualNewValue > premiumAnnualOldValue && premiumNewValue > premiumOldValue)
    return `${name}_increase_out_of_pocket_increase`;
  if (premiumAnnualNewValue < premiumAnnualOldValue && premiumNewValue < premiumOldValue)
    return `${name}_decrease_out_of_pocket_decrease`;
  return `${name}_original`;
};
